import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav, type View } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { can, queryClient, useMachines, useMe, usePros, useServices, type DayHours, type Machine } from '../lib/data.ts';
import { first, initials, isoLocal, money, tone } from '../lib/format.ts';
import { useSettingsEdit } from '../lib/settings.ts';
import { setThemePref, themePref, type ThemePref } from '../lib/theme.ts';
import { CfgChips, CfgNum, CfgText, CfgToggle, Saved } from '../ui/cfg.tsx';
import { BackHeader } from '../ui/Header.tsx';
import { Failed, Skeleton } from '../ui/State.tsx';
import { Icon, type IconName } from '../ui/Icon.tsx';

const DIAS: Array<[keyof import('../lib/data.ts').Settings['hours'], string]> = [['seg', 'Segunda'], ['ter', 'Terça'], ['qua', 'Quarta'], ['qui', 'Quinta'], ['sex', 'Sexta'], ['sab', 'Sábado'], ['dom', 'Domingo']];
const pct = (bps: number) => `${(bps / 100).toFixed(2).replace('.', ',').replace(/,00$/, '')}%`;

// As seções das Configurações e o que ainda falta, num lugar só (celular, lista lateral e visão geral do computador)
interface SetupRow { view: View; icon: IconName; title: string; desc: string; now: string }
function useSetup() {
  const nav = useNav();
  const me = useMe().data!;
  const { settings } = useSettingsEdit();
  const services = useServices().data ?? [];
  const pros = (usePros().data ?? []).filter((p) => p.active);
  const machines = useMachines().data ?? [];
  if (!settings) return null;
  const open = DIAS.filter(([k]) => settings.hours[k].on);
  const groups: Array<[string, SetupRow[]]> = [
    ['O salão', [
      { view: 'salao', icon: 'home', title: 'O salão', desc: 'nome, endereço, WhatsApp e Instagram', now: settings.profile?.address || 'nome e contatos' },
      { view: 'horarios', icon: 'cal', title: 'Horários', desc: 'funcionamento, almoço, folgas e férias', now: open.length ? `${open[0]![0]} a ${open.at(-1)![0]}` : 'fechado' },
      { view: 'servicos', icon: 'polish', title: 'Serviços', desc: 'preço, duração, limpeza e retorno', now: `${services.filter((s) => s.active).length} serviços` },
      { view: 'produtos', icon: 'box2', title: 'Produtos', desc: 'o que o salão vende, preço e estoque', now: 'prateleira do balcão' },
      { view: 'meuLink', icon: 'phone', title: 'Link de agendamento', desc: 'para a cliente marcar sozinha', now: `/agendar/${me.salon.slug}` },
    ]],
    ['Equipe e dinheiro', [
      { view: 'equipe', icon: 'team', title: 'Parceiras', desc: 'cota, bônus e repasse de cada uma', now: `${pros.length} ${pros.length === 1 ? 'pessoa' : 'pessoas'} na agenda` },
      { view: 'pagamentos', icon: 'card', title: 'Formas de pagamento e taxas', desc: 'maquininhas, Pix e pagar depois', now: `${machines.length} ${machines.length === 1 ? 'maquininha' : 'maquininhas'}${settings.pixKey?.trim() ? ' · Pix' : ''}` },
      { view: 'regras', icon: 'alert', title: 'Regras do salão', desc: 'atraso, sinal, falta e cancelamento', now: `tolerância ${settings.toleranceMin} min · cancela até ${settings.cancelWindowHours}h` },
    ]],
    ['Acesso e segurança', [
      { view: 'acessos', icon: 'shield', title: 'Perfis de acesso', desc: 'o que cada pessoa pode ver e fazer', now: 'dona, parceira, recepção' },
      { view: 'historico', icon: 'clip', title: 'Histórico de alterações', desc: 'quem mudou preço, estornou, cancelou ou apagou', now: 'tudo fica registrado' },
    ]],
  ];
  // o que ainda falta para o salão funcionar inteiro (some quando tudo estiver preenchido)
  const missing: Array<[string, string, View]> = [];
  if (!settings.pixKey?.trim()) missing.push(['Chave Pix do salão', 'sem ela, o link de agendamento não pede sinal', 'pagamentos']);
  if (!machines.some((m) => m.active)) missing.push(['Maquininha', 'sem ela, não dá para cobrar no cartão', 'pagamentos']);
  if (!settings.profile?.whatsapp?.trim()) missing.push(['WhatsApp do salão', 'aparece no link para a cliente falar com vocês', 'salao']);
  if (!settings.profile?.address?.trim()) missing.push(['Endereço', 'aparece no link de agendamento', 'salao']);
  return { groups, missing, go: (v: View) => nav.go(v) };
}

function Missing({ s }: { s: NonNullable<ReturnType<typeof useSetup>> }) {
  if (!s.missing.length) return null;
  return (
    <>
      <p className="gl-title first">Antes de começar</p>
      <div className="gl setup-missing">
        {s.missing.map(([t, d, v]) => <button key={t} className="gl-row gl-btn setup-row plain" onClick={() => s.go(v)}><span className="setup-n warn"><Icon name="alert" /></span><b>{t}<small>{d}</small></b><Icon name="chevR" /></button>)}
      </div>
    </>
  );
}

// celular: a lista completa, com o estado de cada seção
export function Setup() {
  const s = useSetup();
  if (!s) return <div className="view-enter cp2"><BackHeader title="Configurações do salão" /></div>;
  return (
    <div className="view-enter cp2"><BackHeader title="Configurações do salão" />
      <div className="cp2-body">
        <Missing s={s} />
        {s.groups.map(([title, rows], gi) => (
          <div key={title}>
            <p className={'gl-title' + (gi === 0 && !s.missing.length ? ' first' : '')}>{title}</p>
            <div className="gl">
              {rows.map((r) => <button key={r.view} className="gl-row gl-btn setup-row plain" onClick={() => s.go(r.view)}><span className="setup-n"><Icon name={r.icon} /></span><b>{r.title}<small>{r.now || r.desc}</small></b><Icon name="chevR" /></button>)}
            </div>
          </div>
        ))}
        <p className="m-hint">Tudo aqui pode ser mudado depois.</p>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

// computador: lista de seções à esquerda, sempre visível (a tela de dentro acende a seção dela).
// Abrir "Configurações" mostra a primeira seção, O salão.
const PARENT: Partial<Record<View, View>> = { setup: 'salao', folgas: 'horarios', servico: 'servicos', parceria: 'equipe', maquininha: 'pagamentos', perfilAcesso: 'acessos', importar: 'salao' };
export function SetupNav({ active: view }: { active: View }) {
  const s = useSetup();
  const active = PARENT[view] ?? view;
  if (!s) return null;
  return (
    <nav className="cfg-nav" aria-label="Seções das configurações">
      <h1 className="cfg-h">Configurações</h1>
      {s.missing.length > 0 && (
        <div className="cfg-todo">
          <p><Icon name="alert" />Falta{s.missing.length > 1 ? 'm' : ''} {s.missing.length} {s.missing.length > 1 ? 'itens' : 'item'}</p>
          {s.missing.map(([t, , v]) => <button key={t} onClick={() => s.go(v)}>{t}</button>)}
        </div>
      )}
      {s.groups.map(([title, rows]) => (
        <div key={title} className="cfg-group">
          <p className="cfg-title">{title}</p>
          {rows.map((r) => (
            <button key={r.view} className={'cfg-item' + (active === r.view ? ' on' : '')} aria-current={active === r.view ? 'page' : undefined} onClick={() => s.go(r.view)}>
              <Icon name={r.icon} /><span>{r.title}</span>
            </button>
          ))}
        </div>
      ))}
    </nav>
  );
}

export function Salao() {
  const me = useMe().data!;
  const toast = useToast();
  const { settings, save, savedAt } = useSettingsEdit();
  const [nameAt, setNameAt] = useState(0);
  if (!settings) return null;
  const p = settings.profile ?? { cnpj: '', address: '', whatsapp: '', instagram: '', google: '' };
  const set = (k: keyof typeof p) => (v: string) => save((s) => ({ ...s, profile: { ...p, ...s.profile, [k]: v } }));
  return (
    <div className="view-enter cp2"><BackHeader title="O salão" label="Configurações" />
      <div className="cp2-body">
        <p className="gl-title">Dados<Saved at={Math.max(savedAt, nameAt)} /></p>
        <div className="gl">
          <CfgText label="Nome" value={me.salon.name} onChange={async (v) => { try { await api.put('/salon', { name: v }); await queryClient.invalidateQueries({ queryKey: ['me'] }); setNameAt(Date.now()); } catch (e) { toast(msg(e), 'warn'); } }} />
          <CfgText label="CNPJ" value={p.cnpj} onChange={set('cnpj')} placeholder="00.000.000/0001-00" />
          <CfgText label="Endereço" value={p.address} onChange={set('address')} placeholder="aparece no link, com mapa" />
          <CfgText label="WhatsApp" value={p.whatsapp} onChange={set('whatsapp')} placeholder="(48) 90000-0000" type="tel" />
          <CfgText label="Instagram" value={p.instagram} onChange={set('instagram')} placeholder="@" />
          <CfgText label="Página no Google" value={p.google} onChange={set('google')} placeholder="link para avaliações" />
        </div>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

function HoraDia({ label, h, onChange }: { label: string; h: DayHours; onChange: (h: DayHours) => void }) {
  return (
    <div className={'hr-dia' + (h.on ? '' : ' off')}><span>{label}</span>
      {h.on ? <>
        <input className="gl-in gl-time" type="time" step={900} value={h.ini} aria-label={`${label}: abre`} onChange={(e) => onChange({ ...h, ini: e.target.value })} />
        <em>às</em>
        <input className="gl-in gl-time" type="time" step={900} value={h.fim} aria-label={`${label}: fecha`} onChange={(e) => onChange({ ...h, fim: e.target.value })} />
      </> : <em className="hr-fech">Fechado</em>}
      <button role="switch" aria-checked={h.on} aria-label={`${label} aberto`} onClick={() => onChange({ ...h, on: !h.on })}><Icon name={h.on ? 'toggleOn' : 'toggleOff'} /></button>
    </div>
  );
}

export function Horarios() {
  const nav = useNav();
  const { settings, save, savedAt } = useSettingsEdit();
  const pros = (usePros().data ?? []).filter((p) => p.active);
  if (!settings) return null;
  return (
    <div className="view-enter cp2"><BackHeader title="Horários" label="Configurações" />
      <div className="cp2-body">
        <p className="gl-title">Funcionamento<Saved at={savedAt} /></p>
        <div className="gl">{DIAS.map(([k, l]) => <HoraDia key={k} label={l} h={settings.hours[k]} onChange={(h) => save((s) => ({ ...s, hours: { ...s.hours, [k]: h } }))} />)}</div>
        <p className="gl-title">Almoço</p>
        <div className="gl">
          <CfgToggle label="Pausa para o almoço" value={settings.lunch.on} onChange={(v) => save((s) => ({ ...s, lunch: { ...s.lunch, on: v } }))} sub={settings.lunch.on ? `das ${settings.lunch.ini} às ${settings.lunch.fim}, ninguém é agendado` : undefined} />
          {settings.lunch.on && (
            <div className="hr-dia"><span>Horário</span>
              <input className="gl-in gl-time" type="time" step={900} value={settings.lunch.ini} aria-label="Almoço começa" onChange={(e) => save((s) => ({ ...s, lunch: { ...s.lunch, ini: e.target.value } }))} /><em>às</em>
              <input className="gl-in gl-time" type="time" step={900} value={settings.lunch.fim} aria-label="Almoço termina" onChange={(e) => save((s) => ({ ...s, lunch: { ...s.lunch, fim: e.target.value } }))} /><span /></div>
          )}
        </div>
        <p className="gl-title">Feriados</p>
        <div className="gl"><CfgToggle label="Fechar nos feriados nacionais" value={settings.holidays} onChange={(v) => save((s) => ({ ...s, holidays: v }))} sub="o link não oferece esses dias" /></div>
        <div className="gl"><CfgToggle label="Tenho recepção" value={!!settings.hasReception} onChange={(v) => save((s) => ({ ...s, hasReception: v }))} sub="ligado: o horário passa por Chegou e Começar. Desligado: vai direto para Receber" /></div>
        <div className="gl"><CfgToggle label="Permitir horários sobrepostos" value={!!settings.allowOverlap} onChange={(v) => save((s) => ({ ...s, allowOverlap: v }))} sub="marcar e remarcar em cima de outro horário, a qualquer hora" /></div>
        <p className="gl-title">Folgas, férias e bloqueios</p>
        <div className="gl">
          {pros.map((p) => {
            const off = DIAS.filter(([k]) => p.workHours?.[k]?.on === false).map(([k]) => k);
            return (
              <button key={p.id} className="gl-row gl-btn ac-member" onClick={() => nav.go('folgas', { id: p.id })}>
                <span className={`av t-${tone(p.id)}`}>{initials(p.name)}</span><b>{p.name}<small>{off.length ? `folga: ${off.join(', ')}` : 'atende nos dias do salão'}</small></b><Icon name="chevR" />
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

interface Block { id: string; reason: string; from: { date: string; time: string }; to: { date: string; time: string } }

export function Folgas() {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const me = useMe().data!;
  const pros = usePros().data ?? [];
  const p = pros.find((x) => x.id === nav.params.id);
  const blocks = useQuery({ queryKey: ['blocks', nav.params.id], queryFn: () => api.get<Block[]>(`/blocks?professionalId=${nav.params.id}`) });
  const [savedAt, setSavedAt] = useState(0);
  const [arm, setArm] = useState<string | null>(null);
  if (!p) return <div className="view-enter cp2"><BackHeader title="Folgas" label="Horários" /></div>;
  const hours = me.salon.settings.hours;
  async function flipDay(k: string) {
    const wh = { ...(p!.workHours ?? {}) };
    if (wh[k]?.on === false) delete wh[k]; else wh[k] = { on: false, ini: '00:00', fim: '00:00' };
    try {
      const full = await api.get<Array<Record<string, unknown>>>('/professionals');
      const cur = full.find((x) => x.id === p!.id)!;
      await api.put(`/professionals/${p!.id}`, { ...pick(cur), workHours: wh });
      await qc.invalidateQueries({ queryKey: ['professionals'] });
      setSavedAt(Date.now());
    } catch (e) { toast(msg(e), 'warn'); }
  }
  const fmt = (b: Block) => {
    const one = b.from.date === b.to.date || (b.to.time === '00:00' && b.from.time === '00:00' && b.from.date === addDay(b.to.date, -1));
    const d = (x: string) => `${x.slice(8)}/${x.slice(5, 7)}`;
    if (b.from.time === '00:00' && b.to.time === '00:00') return one ? `${d(b.from.date)} · dia inteiro` : `${d(b.from.date)} a ${d(addDay(b.to.date, -1))}`;
    return `${d(b.from.date)} · ${b.from.time} às ${b.to.time}`;
  };
  return (
    <div className="view-enter cp2"><BackHeader title={p.name} label="Horários" />
      <div className="cp2-body">
        <p className="gl-title">Dias que {first(p.name)} atende<Saved at={savedAt} /></p>
        <div className="gl"><div className="gl-block"><div className="pf-days">
          {DIAS.map(([k]) => {
            const on = p.workHours?.[k]?.on !== false, aberto = hours[k].on;
            return <button key={k} className={on && aberto ? 'on' : ''} aria-pressed={on && aberto} disabled={!aberto} title={aberto ? undefined : 'O salão fecha neste dia'} onClick={() => flipDay(k)}>{k === 'sab' ? 'sáb' : k}</button>;
          })}
        </div></div><p className="m-hint rg-hint">Os outros dias são folga fixa. Dias em que o salão fecha ficam apagados.</p></div>
        <p className="gl-title">Férias e bloqueios</p>
        <div className="gl">
          {(blocks.data ?? []).length ? blocks.data!.map((b) => (
            <div key={b.id} className="gl-row bq-row"><span>{b.reason || 'Bloqueio'}</span><b>{fmt(b)}</b>
              <button className="gl-act" onClick={async () => {
                if (arm !== b.id) return setArm(b.id);
                setArm(null);
                try { await api.post(`/blocks/${b.id}/remove`); await blocks.refetch(); await qc.invalidateQueries({ queryKey: ['availability'] }); toast('Bloqueio removido. Os horários voltam a aparecer livres.'); } catch (e) { toast(msg(e), 'warn'); }
              }}>{arm === b.id ? 'Confirmar' : 'Tirar'}</button></div>
          )) : <p className="m-hint rg-hint" style={{ marginTop: 12 }}>Nenhum bloqueio.</p>}
          <button className="gl-row gl-btn ac-invite" onClick={() => nav.open({ kind: 'bloqueio', professionalId: p.id })}><b>+ Folga, férias ou bloqueio</b><Icon name="chevR" /></button>
        </div>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

export function pick(p: Record<string, unknown>) {
  return { name: p.name, role: p.role, color: p.color, cota: p.cota, productBps: p.productBps, payoutPeriod: p.payoutPeriod, workHours: p.workHours, document: p.document, pixKey: p.pixKey, active: p.active, version: p.version };
}
const addDay = (d: string, n: number) => { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };

export function Regras() {
  const nav = useNav();
  const { settings: s, save, savedAt } = useSettingsEdit();
  if (!s) return null;
  const d = s.deposit;
  const setD = (patch: Partial<typeof d>) => save((x) => ({ ...x, deposit: { ...x.deposit, ...patch } }));
  return (
    <div className="view-enter cp2"><BackHeader title="Regras do salão" label="Configurações" />
      <div className="cp2-body">
        <p className="ac-intro">Valem para todas as parceiras e para o agendamento online.</p>
        <p className="gl-title">Agendamento pelo link<Saved at={savedAt} /></p>
        <div className="gl">
          <CfgToggle label="Clientes podem agendar pelo link" value={s.onlineBooking !== false} onChange={(v) => save((x) => ({ ...x, onlineBooking: v }))}
            sub={s.onlineBooking !== false ? 'O link mostra os horários livres e reserva na hora.' : 'Pausado: o link avisa que o agendamento online está fechado e mostra o WhatsApp do salão.'} />
        </div>
        <p className="gl-title">Atraso</p>
        <div className="gl">
          <CfgNum label="Tolerância" unit="min" value={s.toleranceMin} lim={[0, 60, 5]} onChange={(v) => save((x) => ({ ...x, toleranceMin: v }))} />
          <p className="m-hint rg-hint">Até lá, o cartão mostra "tolerância até…". Depois, "passou da tolerância", com "Não veio" à mão.</p>
        </div>
        <p className="gl-title">Sinal</p>
        <div className="gl">
          <div className="gl-block"><span>Pedir sinal</span><CfgChips value={d.mode} options={[['never', 'Nunca'], ['after_no_shows', 'Depois de faltas'], ['always', 'Sempre']]} onChange={(v) => setD({ mode: v })} /></div>
          {d.mode === 'after_no_shows' && <CfgNum label="A partir da" unit="ª falta" value={d.afterNoShows} lim={[1, 10, 1]} onChange={(v) => setD({ afterNoShows: v })} />}
          {d.mode !== 'never' && <>
            <div className="gl-block"><span>Valor do sinal</span><CfgChips value={d.kind} options={[['pct', '% do serviço'], ['fixed', 'Valor fixo']]} onChange={(v) => setD({ kind: v })} /></div>
            {d.kind === 'fixed' ? <CfgNum label="Valor" kind="money" value={d.fixedCents} lim={[500, 50_000, 500]} onChange={(v) => setD({ fixedCents: v })} />
              : <CfgNum label="Porcentagem" unit="%" value={Math.round(d.bps / 100)} lim={[5, 100, 5]} onChange={(v) => setD({ bps: v * 100 })} />}
          </>}
          <CfgNum label="Prazo para o Pix do sinal" unit="h" value={d.deadlineHours} lim={[1, 24, 1]} onChange={(v) => setD({ deadlineHours: v })} />
        </div>
        <p className="gl-title">No link de agendamento</p>
        <div className="gl">
          <CfgNum label="Marcar com pelo menos" unit="h antes" value={s.minAdvanceHours} lim={[0, 72, 1]} onChange={(v) => save((x) => ({ ...x, minAdvanceHours: v }))} />
          <CfgNum label="Até" unit="dias à frente" value={s.maxAdvanceDays} lim={[7, 180, 7]} onChange={(v) => save((x) => ({ ...x, maxAdvanceDays: v }))} />
        </div>
        <p className="gl-title">Falta e cancelamento</p>
        <div className="gl">
          <CfgNum label="Cancelar sem custo até" unit="h antes" value={s.cancelWindowHours} lim={[0, 168, 6]} onChange={(v) => save((x) => ({ ...x, cancelWindowHours: v }))} />
          <div className="gl-row rg-rule"><span>Cancelou no prazo</span><b>O sinal vira crédito para a cliente. A parceira não recebe</b></div>
          <div className="gl-row rg-rule"><span>Faltou ou cancelou fora do prazo</span><b>O sinal fica retido</b></div>
          <div className="gl-block"><span>O sinal retido</span><CfgChips value={d.retainedSplit} options={[['split', 'Dividido pela cota da parceira'], ['salon_only', 'Fica só com o salão']]} onChange={(v) => setD({ retainedSplit: v })} /></div>
          <div className="gl-row rg-rule"><span>Devolver o sinal</span><b>Só em caso excepcional, com motivo. A parceira pode fazer</b></div>
        </div>
        <button className="link-back" onClick={() => nav.go('pagamentos')}>Formas de pagamento e taxas</button>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

export function Pagamentos() {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const { settings: s, save, savedAt } = useSettingsEdit();
  const machines = useMachines().data ?? [];
  const pros = usePros().data ?? [];
  if (!s) return null;
  const M = s.methods;
  const m0 = machines[0];
  const partner = pros.find((p) => !p.isOwner && p.cota?.kind === 'fixed');
  const pp = partner?.cota && partner.cota.kind === 'fixed' ? partner.cota.bps / 10_000 : 0.5;
  const fee = m0 ? Math.round((10_000 * m0.creditBps) / 10_000) : 0;
  const parte = s.feeMode === 'before_split' ? Math.round((10_000 - fee) * pp) : Math.round(10_000 * pp);
  async function addMachine() {
    try {
      const m = await api.post<Machine>('/card-machines', { name: 'Nova maquininha', debitBps: 100, creditBps: 300, installmentBps: { '2': 450, '3': 500 } });
      await qc.invalidateQueries({ queryKey: ['machines'] });
      nav.go('maquininha', { id: m.id });
    } catch (e) { toast(msg(e), 'warn'); }
  }
  return (
    <div className="view-enter cp2"><BackHeader title="Formas de pagamento e taxas" label="Configurações" />
      <div className="cp2-body">
        <p className="ac-intro">O que o salão aceita e quanto cada forma custa. A taxa nunca aparece para a cliente.</p>
        <p className="gl-title">Formas aceitas<Saved at={savedAt} /></p>
        <div className="gl">
          {([['pix', 'Pix'], ['credit', 'Crédito'], ['debit', 'Débito'], ['cash', 'Dinheiro']] as const).map(([k, l]) => <CfgToggle key={k} label={l} value={M[k]} onChange={(v) => save((x) => ({ ...x, methods: { ...x.methods, [k]: v } }))} />)}
          <CfgToggle label="Pagar depois (fiado)" value={M.later} onChange={(v) => save((x) => ({ ...x, methods: { ...x.methods, later: v } }))} sub="fica como pendência na ficha da cliente" />
          {M.later && <CfgNum label="Limite por cliente" kind="money" value={s.laterLimitCents} lim={[1000, 500_000, 1000]} onChange={(v) => save((x) => ({ ...x, laterLimitCents: v }))} />}
          <CfgToggle label="Crédito parcelado" value={s.installments.on} onChange={(v) => save((x) => ({ ...x, installments: { ...x.installments, on: v } }))} />
          {s.installments.on && <>
            <CfgNum label="Até" unit="vezes" value={s.installments.max} lim={[2, 12, 1]} onChange={(v) => save((x) => ({ ...x, installments: { ...x.installments, max: v } }))} />
            <div className="gl-block"><span>Juros do parcelado</span><CfgChips value={s.installments.interest} options={[['salon', 'O salão paga'], ['client', 'A cliente paga']]} onChange={(v) => save((x) => ({ ...x, installments: { ...x.installments, interest: v } }))} /></div>
          </>}
        </div>
        <p className="gl-title">Maquininhas</p>
        <div className="gl">
          {machines.map((m) => (
            <button key={m.id} className="gl-row gl-btn pm-maq" onClick={() => nav.go('maquininha', { id: m.id })}>
              <b>{m.name}<small>débito {pct(m.debitBps)} · crédito {pct(m.creditBps)}{m.active ? '' : ' · desativada'}</small></b><Icon name="chevR" /></button>
          ))}
          <button className="gl-row gl-btn ac-invite" onClick={addMachine}><b>+ Adicionar maquininha</b></button>
        </div>
        <p className="gl-title">Pix</p>
        <div className="gl">
          <CfgText label="Chave Pix do salão" value={s.pixKey} onChange={(v) => save((x) => ({ ...x, pixKey: v }))} />
          <p className="m-hint rg-hint">A cliente paga o sinal do link nessa chave, pelo banco dela. A equipe confere e confirma no app.</p>
        </div>
        <p className="gl-title">A taxa na divisão com a parceira</p>
        <div className="gl"><div className="gl-block"><CfgChips value={s.feeMode} options={[['salon', 'Sai só da parte do salão'], ['before_split', 'Sai antes da divisão']]} onChange={(v) => save((x) => ({ ...x, feeMode: v }))} /></div>
          {m0 && <p className="m-hint rg-hint pm-ex">Exemplo: R$ 100,00 no crédito ({m0.name}, {pct(m0.creditBps)}). Cai {money(10_000 - fee)}. A parceira com cota de {Math.round(pp * 100)}% recebe {money(parte)} e o salão fica com {money(10_000 - fee - parte)}.</p>}
        </div>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

export function Maquininha() {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const { settings } = useSettingsEdit();
  const machines = useMachines().data ?? [];
  const m = machines.find((x) => x.id === nav.params.id) as (Machine & { debitDays: number; creditDays: number }) | undefined;
  const [savedAt, setSavedAt] = useState(0);
  if (!m || !settings) return <div className="view-enter cp2"><BackHeader title="Maquininha" label="Pagamentos" /></div>;
  const save = async (patch: Partial<typeof m>) => {
    try { await api.put(`/card-machines/${m.id}`, { name: m.name, debitBps: m.debitBps, creditBps: m.creditBps, installmentBps: m.installmentBps, debitDays: m.debitDays, creditDays: m.creditDays, active: m.active, ...patch }); await qc.invalidateQueries({ queryKey: ['machines'] }); setSavedAt(Date.now()); }
    catch (e) { toast(msg(e), 'warn'); }
  };
  const parcs = Array.from({ length: Math.max(0, settings.installments.max - 1) }, (_, i) => i + 2);
  return (
    <div className="view-enter cp2"><BackHeader title={m.name} label="Pagamentos" />
      <div className="cp2-body">
        <p className="gl-title">Taxas<Saved at={savedAt} /></p>
        <div className="gl">
          <CfgText label="Nome" value={m.name} onChange={(v) => save({ name: v || 'Maquininha' })} />
          <CfgNum label="Débito" unit="%" kind="bps" value={m.debitBps} lim={[0, 1000, 5]} onChange={(v) => save({ debitBps: v })} />
          <CfgNum label="Crédito à vista" unit="%" kind="bps" value={m.creditBps} lim={[0, 1000, 5]} onChange={(v) => save({ creditBps: v })} />
          {settings.installments.on && parcs.map((n) => (
            <CfgNum key={n} label={`Crédito em ${n}x`} unit="%" kind="bps" value={m.installmentBps[String(n)] ?? m.creditBps} lim={[0, 2000, 5]} onChange={(v) => save({ installmentBps: { ...m.installmentBps, [String(n)]: v } })} />
          ))}
        </div>
        <p className="gl-title">Quando o dinheiro cai</p>
        <div className="gl">
          <CfgNum label="Débito" unit="dias" value={m.debitDays} lim={[0, 30, 1]} onChange={(v) => save({ debitDays: v })} />
          <CfgNum label="Crédito" unit="dias" value={m.creditDays} lim={[0, 60, 1]} onChange={(v) => save({ creditDays: v })} />
          <p className="m-hint rg-hint">Use 1 se a maquininha antecipa e paga no dia seguinte.</p>
        </div>
        <button className="link-back pm-del" onClick={async () => { await save({ active: !m.active }); if (m.active) nav.back(); }}>{m.active ? 'Desativar esta maquininha' : 'Reativar esta maquininha'}</button>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

const AREA: Record<string, string> = { appointment: 'Agenda', order: 'Cobrança', client: 'Cliente', cash: 'Caixa', tenant: 'Configuração', service: 'Serviço', professional: 'Parceira', card_machine: 'Maquininha', role: 'Perfil de acesso', member: 'Equipe', block: 'Bloqueio', import: 'Importação', user: 'Conta' };
const ACTION: Record<string, string> = {
  'appointment.created': 'marcou um horário', 'appointment.cancelled': 'cancelou um horário', 'appointment.rescheduled': 'remarcou um horário', 'appointment.no_show': 'registrou uma falta',
  'appointment.arrived': 'registrou a chegada', 'appointment.in_service': 'iniciou um atendimento', 'order.paid': 'recebeu uma cobrança', 'order.reversed': 'estornou uma cobrança',
  'client.created': 'cadastrou uma cliente', 'client.updated': 'alterou os dados de uma cliente', 'client.anamnesis': 'salvou uma anamnese', 'client.lgpd_erased': 'apagou os dados de uma cliente a pedido',
  'client.anamnesis_erased': 'apagou uma anamnese a pedido', 'client.exported': 'exportou os dados de uma cliente', 'cash.opened': 'abriu o caixa', 'cash.closed': 'fechou o caixa',
  'cash.withdrawal': 'registrou uma sangria', 'cash.supply': 'registrou um suprimento', 'settings.updated': 'mudou as regras do salão', 'service.updated': 'alterou um serviço',
  'service.created': 'criou um serviço', 'professional.updated': 'alterou uma parceira', 'role.updated': 'mudou um perfil de acesso', 'role.created': 'criou um perfil de acesso',
  'member.invited': 'convidou uma pessoa', 'member.revoked': 'tirou o acesso de uma pessoa', 'member.updated': 'mudou o perfil de uma pessoa', 'partner.payout': 'pagou um repasse',
  'deposit.received': 'confirmou um sinal', 'deposit.refunded': 'devolveu um sinal', 'debt.paid': 'recebeu uma pendência', 'block.created': 'bloqueou a agenda', 'block.removed': 'tirou um bloqueio',
};

export function Historico() {
  const q = useQuery({ queryKey: ['audit'], queryFn: () => api.get<Array<{ id: string; actor: string; entityType: string; action: string; reason: string; createdAt: string }>>('/audit?limit=200') });
  return (
    <div className="view-enter cp2"><BackHeader title="Histórico de alterações" label="Configurações" />
      <div className="cp2-body">
        <p className="ac-intro">Tudo o que muda dinheiro, agenda ou dados de cliente fica registrado aqui, com quem fez e quando. Não dá para apagar.</p>
        <div className="timeline">
          {(q.data ?? []).map((e) => {
            const t = isoLocal(e.createdAt);
            return <div key={e.id} className="tl-row"><time>{t.date.slice(8)}/{t.date.slice(5, 7)} {t.time}</time><span>{first(e.actor.split('<')[0]!.trim()) || e.actor}<small>{ACTION[e.action] ?? e.action}{e.reason ? ` · ${e.reason}` : ''}</small></span><b>{AREA[e.entityType] ?? ''}</b></div>;
          })}
          {q.data === undefined && (q.isError ? <Failed compact error={q.error} retry={() => q.refetch()} /> : <Skeleton rows={4} />)}
        </div>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

export function Perfil() {
  const me = useMe().data!;
  const toast = useToast();
  const [savedAt, setSavedAt] = useState(0);
  const [theme, setTheme] = useState<ThemePref>(themePref);
  const [pw, setPw] = useState({ open: false, current: '', next: '' });
  const applyTheme = (t: ThemePref) => { setTheme(t); setThemePref(t); };
  return (
    <div className="view-enter cp2"><BackHeader title="Meu perfil" />
      <div className="cp2-body">
        <div className="cp2-id"><span className={`av t-${tone(me.user.id)}`}>{initials(me.user.name)}</span><div><h1>{me.user.name}</h1><p>{me.user.email}</p></div></div>
        <p className="gl-title">Dados<Saved at={savedAt} /></p>
        <div className="gl"><CfgText label="Nome" value={me.user.name} onChange={async (v) => { try { await api.put('/me', { name: v }); await queryClient.invalidateQueries(); setSavedAt(Date.now()); } catch (e) { toast(msg(e), 'warn'); } }} /></div>
        <p className="gl-title">Aparência</p>
        <div className="gl pf-theme"><div className="gl-block"><span>Tema</span><CfgChips value={theme} options={[['auto', 'Automático'], ['light', 'Claro'], ['dark', 'Escuro']]} onChange={applyTheme} /></div>
          <p className="m-hint rg-hint">Automático segue o tema do celular. Vale para este aparelho.</p></div>
        <p className="gl-title">Conta</p>
        <div className="gl">
          <button className="gl-row gl-btn" onClick={() => setPw({ ...pw, open: !pw.open })}><span>Senha</span><b>Trocar senha</b><Icon name="chevR" /></button>
          {pw.open && (
            <div className="adj-panel">
              <div className="field"><label htmlFor="pwCur">Senha atual</label><input className="text-in" id="pwCur" type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} /></div>
              <div className="field"><label htmlFor="pwNew">Nova senha (pelo menos 10 caracteres)</label><input className="text-in" id="pwNew" type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} /></div>
              <button className="cta" disabled={!pw.current || pw.next.length < 10} onClick={async () => {
                try { await api.post('/me/password', { current: pw.current, next: pw.next }); setPw({ open: false, current: '', next: '' }); toast('Senha trocada. Os outros aparelhos vão pedir para entrar de novo.'); } catch (e) { toast(msg(e), 'warn'); }
              }}>Trocar senha</button>
            </div>
          )}
          <button className="gl-row gl-btn pf-out" onClick={async () => { await api.post('/auth/logout').catch(() => undefined); queryClient.clear(); window.location.reload(); }}><span /><b>Sair desta conta</b><Icon name="logout" /></button>
        </div>
        <p className="m-hint">As mudanças ficam salvas na hora.{can(me, 'config') ? '' : ''}</p>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}
